import { inject, Injectable, signal } from '@angular/core';
import { ThemeInterface } from '../../theme/theme.interface';
import { TOKEN } from '../constants/token.constant';
import { DOCUMENT } from '@angular/common';
import { firstValueFrom, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, filter, map, startWith } from 'rxjs/operators';
import { ROUTING } from '../constants/routing.constants';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly _themeSig = signal<ThemeInterface>({} as ThemeInterface);
  readonly themeSig = this._themeSig.asReadonly();
  readonly isFsRoute = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() =>
        [ROUTING.FREE_SIGNATURES, ROUTING.FREE_SIGNATURES_COMMERCIAL].some((c) =>
          this.router.url.includes(c)
        )
      ),
      startWith(
        [ROUTING.FREE_SIGNATURES, ROUTING.FREE_SIGNATURES_COMMERCIAL].some((c) =>
          this.router.url.includes(c)
        )
      )
    )
  );
  private readonly appliedStyleNames = new Set<string>();

  async init(): Promise<boolean> {
    await this.update(this.templateId);
    return Promise.resolve(true);
  }

  async update(templateId: string | null = ''): Promise<void> {
    const next = await this.getThemeDefinition(templateId);

    this.clearCssVar();

    if (next == null) {
      this._themeSig.set({} as ThemeInterface);
      return;
    }

    this._themeSig.set(next);

    if (next.style) {
      this.setCssVar(next.style);
    }
  }

  updateThemeKey(val: Partial<ThemeInterface>) {
    const current = this._themeSig();
    if (!current) return;

    const next: ThemeInterface = { ...current, ...val };
    this._themeSig.set(next);

    if (val.style) {
      this.clearCssVar();
      this.setCssVar(next.style ?? {});
    }
  }

  private setCssVar(style: Record<string, string>): void {
    Object.entries(style).forEach(([key, val]) => {
      this.appliedStyleNames.add(key);
      this.setProperty(key, val);
    });
  }

  private clearCssVar() {
    this.appliedStyleNames.forEach((propName) => this.removeProperty(propName));
    this.appliedStyleNames.clear();
  }

  private get templateId(): string {
    return sessionStorage.getItem(TOKEN.TEMPLATE_ID) || '';
  }

  private async getThemeDefinition(templateId: string | null): Promise<ThemeInterface | null> {
    if (!templateId) return null;

    const path = `/config/themes/${templateId}.json`;
    return await firstValueFrom(
      this.http.get<ThemeInterface>(path).pipe(catchError(() => of(null)))
    );
  }

  private setProperty(propName: string, propVal: any, el = this.document.documentElement) {
    el.style.setProperty(propName, propVal, 'important');
  }

  private removeProperty(propName: string, el = this.document.documentElement): void {
    el.style.removeProperty(propName);
  }
}
